/**
 * listingFilter — progressive enhancement for the home-design listing filter
 * bar. The form is a real <form method="GET" data-swup-form>; with JS, changing
 * any control auto-submits, and @swup/forms-plugin turns that GET submit into a
 * Swup visit (no full reload — #swup re-renders the grid + the controls'
 * selected state from the new query string). With JS off, the <noscript> Apply
 * button submits natively. The component owns no filter state — the URL is the
 * single source of truth, rendered server-side by the ListingFilters tag.
 *
 * ⚠️ A facet is rendered TWICE: once in the filter bar and once in the "More
 * Filters" modal, whose selects join this form through the `form` attribute
 * (MOHO-104 keeps the two in lockstep). Both copies therefore serialize, and
 * PHP keeps the LAST value for a repeated name — so a form with both copies
 * populated submits `?storey_count=1-storey&storey_count=2-storey` and the
 * modal's stale copy silently overrides the control the visitor just used. The
 * filter looked frozen: the first change worked, every change after it did
 * nothing. MOHO-161 patched the Reset half of the same fault; this is the rest
 * of it.
 *
 * Two rules fix it, and both are needed:
 *  1. mirror() keeps every copy of a facet on the same value, the moment one
 *     of them changes — including the modal's, which does not auto-submit.
 *  2. apply() serializes each facet name once.
 */
export default function listingFilter() {
    return {
        _sync: null,

        init() {
            // Capture phase, on the document: the modal's selects sit OUTSIDE
            // this form in the DOM, so their change events never bubble through
            // it, and capture runs before the bar dropdowns' own @change="apply"
            // — so apply() always serializes a form whose copies agree.
            this._sync = (event) => { this.mirror(event.target); };
            document.addEventListener('change', this._sync, true);
        },

        destroy() {
            document.removeEventListener('change', this._sync, true);
        },

        /**
         * Copy a changed control's value onto every other control that submits
         * the same facet. Every filter control is a <select>, so assigning
         * `value` is enough.
         */
        mirror(source) {
            if (! source || ! source.name) {
                return;
            }

            const controls = [...this.$root.elements];

            if (! controls.includes(source)) {
                return;
            }

            controls.forEach((el) => {
                if (el !== source && el.name === source.name) {
                    el.value = source.value;
                }
            });
        },

        /** Submit the owning form (fired on control change, and by the modal's Done). */
        apply() {
            // Keep out of the query string: a facet with no value (it shouldn't
            // appear as `&bedrooms_min=`) and every copy of a facet after the
            // first (see the header — a repeated name lets the last copy win).
            // Disabled fields aren't serialized, so disable those for the
            // duration of the (synchronous) submit serialization and re-enable
            // them in the same tick — nothing repaints in between, so no flicker.
            // requestSubmit() dispatches the cancelable submit event
            // @swup/forms-plugin intercepts; .submit() would bypass it.
            const claimed = new Set();

            const muted = [...this.$root.elements].filter((el) => {
                if (! el.name || el.disabled) {
                    return false;
                }

                if (el.value === '' || claimed.has(el.name)) {
                    return true;
                }

                claimed.add(el.name);

                return false;
            });

            muted.forEach((el) => { el.disabled = true; });
            this.$root.requestSubmit();
            muted.forEach((el) => { el.disabled = false; });
        },
    };
}
